/* Public storefront UI. The server owns memory, model access, product grounding and email delivery. */
(() => {
  'use strict';
  const root = document.querySelector('[data-shopping-assistant]');
  if (!root || root.dataset.ready) return;
  const dialog = root.querySelector('[data-chat-dialog]');
  if (!dialog || typeof dialog.showModal !== 'function' || !window.crypto?.randomUUID) return;
  let endpoint;
  try {
    endpoint = new URL(root.dataset.endpoint);
    if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password || endpoint.search || endpoint.hash) return;
    endpoint = endpoint.href.replace(/\/+$/, '');
  } catch (_) { return; }
  root.dataset.ready = 'true';
  root.hidden = false;
  const q = selector => root.querySelector(selector);
  const welcome = q('[data-chat-welcome]');
  const conversation = q('[data-chat-conversation]');
  const messages = q('[data-chat-messages]');
  const input = q('[data-chat-input]');
  const send = q('[data-chat-send]');
  const start = q('[data-chat-start]');
  const notice = q('[data-chat-notice]');
  const handoff = q('[data-chat-handoff]');
  const confirm = q('[data-chat-confirm]');
  const quick = q('[data-chat-quick]');
  const storageKey = 'casitu.shopping-assistant.session.v1';
  let token = '', pending = null, busy = false, restored = false, finished = false;
  let restorePromise = null, returnFocus = null, typing = null, sentToTeam = false;
  try { token = sessionStorage.getItem(storageKey) || ''; } catch (_) { /* Storage can be unavailable in private browsing. */ }
  if (!/^[A-Za-z0-9._~-]{20,2048}$/.test(token)) token = '';

  function remember(value) {
    token = value;
    try { if (value) sessionStorage.setItem(storageKey, value); else sessionStorage.removeItem(storageKey); } catch (_) { /* In-memory sessions still work. */ }
  }
  function element(tag, className, text) {
    const result = document.createElement(tag);
    if (className) result.className = className;
    if (text !== undefined) result.textContent = text;
    return result;
  }
  function showNotice(text, retry) {
    notice.replaceChildren();
    notice.hidden = !text;
    if (!text) return;
    notice.append(document.createTextNode(text));
    if (retry) {
      const button = element('button', '', 'Reintentar');
      button.type = 'button';
      button.addEventListener('click', retry, {once:true});
      notice.append(button);
    }
  }
  function safeLink(value) {
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.hostname !== 'casitu3d.com' || url.username || url.password || url.port) return null;
      if (!/^\/(products|pages|policies|collections)\/[a-z0-9][a-z0-9/_-]*$/i.test(url.pathname)) return null;
      url.hash = '';
      // Only a real Shopify variant may be retained; arbitrary query strings are not navigation instructions.
      const variant = url.searchParams.get('variant');
      url.search = '';
      if (variant && /^\d{6,20}$/.test(variant)) url.searchParams.set('variant', variant);
      return url.href;
    } catch (_) { return null; }
  }
  function safeImage(value) {
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
      if (url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/')) return url.href;
      if (url.hostname === 'casitu3d.com' && url.pathname.startsWith('/cdn/shop/')) return url.href;
      return null;
    } catch (_) { return null; }
  }
  function richText(target, value) {
    const text = String(value || '').slice(0, 12000);
    // Markdown links are interpreted narrowly; all HTML and all other Markdown remain plain text.
    const pattern = /\[([^\]\n]{1,180})\]\((https:\/\/[^\s)]+)\)|https:\/\/[^\s<>]+/g;
    let previous = 0, match;
    while ((match = pattern.exec(text))) {
      target.append(document.createTextNode(text.slice(previous, match.index)));
      const raw = match[2] || match[0];
      const trimmed = raw.replace(/[.,;!?]+$/, '');
      const href = safeLink(trimmed);
      if (href) {
        const anchor = element('a', '', match[1] || trimmed);
        anchor.href = href;
        target.append(anchor);
        if (!match[2]) target.append(document.createTextNode(raw.slice(trimmed.length)));
      } else target.append(document.createTextNode(match[0]));
      previous = pattern.lastIndex;
    }
    target.append(document.createTextNode(text.slice(previous)));
  }
  function referenceRoutes(product) {
    const approved = safeLink(product.url);
    if (!approved) return null;
    const handle = new URL(approved).pathname.match(/^\/products\/([a-z0-9][a-z0-9-]{0,150})$/)?.[1];
    if (!handle || (product.handle !== undefined && product.handle !== handle)) return null;
    try {
      const prefix = window.Shopify?.routes?.root;
      if (typeof prefix !== 'string' || !/^\/(?:[a-z]{2}(?:-[a-z0-9]{2,8})?\/)?$/i.test(prefix)) return null;
      const base = new URL(prefix, location.origin);
      if (base.protocol !== 'https:' || base.origin !== location.origin) return null;
      return {handle, api:new URL('products/' + handle + '.js', base).href,
        href:new URL('products/' + handle, base).href, catalog:new URL('pages/figuras', base).href};
    } catch (_) { return null; }
  }
  async function loadReference(routes) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 6000);
    try {
      // Official same-origin Shopify Ajax GET: never use the chat request helper or send its bearer.
      const response = await fetch(routes.api, {method:'GET', headers:{Accept:'application/json'},
        credentials:'same-origin', redirect:'manual', cache:'no-store', signal:controller.signal});
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('catalog_unavailable');
      const product = await response.json();
      if (!product || product.handle !== routes.handle || typeof product.available !== 'boolean') throw new Error('invalid_product');
      if (!product.available) return null;
      if (!Array.isArray(product.variants) || product.variants.length > 250) throw new Error('invalid_variants');
      const variants = product.variants.filter(variant => variant && variant.available === true);
      if (!variants.length) return null;
      if (variants.some(variant => !Number.isSafeInteger(variant.price) || variant.price < 0)) throw new Error('invalid_price');
      if (typeof product.title !== 'string' || !product.title.trim()) throw new Error('invalid_title');
      const rawImage = product.featured_image || product.images?.[0];
      let image = null;
      try { if (typeof rawImage === 'string') image = safeImage(new URL(rawImage, location.origin).href); } catch (_) { /* An image is optional. */ }
      return {title:product.title.trim().slice(0,160), cents:Math.min(...variants.map(variant => variant.price)), image};
    } catch (_) { return {unverified:true}; }
    finally { clearTimeout(timeout); }
  }
  function referencePrice(cents) {
    const currency = root.dataset.currency;
    try {
      const known = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('currency') : ['EUR'];
      if (!known.includes(currency)) return '';
      return 'Desde ' + new Intl.NumberFormat('es-ES', {style:'currency', currency}).format(cents / 100);
    } catch (_) { return ''; }
  }
  async function addReference(slot, list, target, routes) {
    const product = await loadReference(routes);
    // Expiry, restore and explicit deletion detach old bubbles; late responses must stay detached.
    if (!slot.isConnected || !target.isConnected) return;
    if (!product) {
      slot.remove();
      if (!list.childElementCount) list.remove();
      return;
    }
    const card = element('a', 'shopping-assistant__product');
    card.href = product.unverified ? routes.catalog : routes.href;
    if (product.image) {
      const image = element('img');
      image.src = product.image; image.alt = ''; image.width = 63; image.height = 72;
      image.loading = 'lazy'; image.referrerPolicy = 'no-referrer';
      image.addEventListener('error', () => image.remove(), {once:true});
      card.append(image);
    }
    const description = element('span');
    description.append(element('strong', '', product.unverified ? 'Consultar catálogo' : product.title));
    const price = product.unverified ? '' : referencePrice(product.cents);
    if (price) description.append(element('small', '', price));
    description.append(element('em', '', product.unverified ? 'Ver catálogo →' : price ? 'Ver producto →' : 'Consultar ficha →'));
    card.append(description);
    slot.replaceWith(card);
    // Do not scroll: customers may be reading an earlier message while the public GET completes.
  }
  function addProducts(target, products) {
    if (!Array.isArray(products)) return;
    const list = element('div', 'shopping-assistant__products');
    products.slice(0, 4).forEach(product => {
      if (!product || typeof product !== 'object') return;
      if (product.referenceOnly === true) {
        const routes = referenceRoutes(product);
        if (!routes) return;
        const slot = element('div');
        slot.hidden = true;
        list.append(slot);
        addReference(slot, list, target, routes);
        return;
      }
      const href = safeLink(product.url);
      if (!href || !new URL(href).pathname.startsWith('/products/')) return;
      const card = element('a', 'shopping-assistant__product');
      card.href = href;
      const source = safeImage(product.image);
      if (source) {
        const image = element('img');
        image.src = source;
        image.alt = '';
        image.width = 63;
        image.height = 72;
        image.loading = 'lazy';
        image.referrerPolicy = 'no-referrer';
        image.addEventListener('error', () => image.remove(), {once:true});
        card.append(image);
      }
      const description = element('span');
      description.append(element('strong', '', String(product.title || 'Ver producto').slice(0, 160)));
      const price = typeof product.price === 'number' && Number.isFinite(product.price)
        ? new Intl.NumberFormat('es-ES', {style:'currency', currency:'EUR'}).format(product.price)
        : typeof product.price === 'string' ? product.price.slice(0, 40) : '';
      if (price) description.append(element('small', '', price));
      description.append(element('em', '', 'Ver producto →'));
      card.append(description);
      list.append(card);
    });
    if (list.childElementCount) target.append(list);
  }
  function scrollLatest() { requestAnimationFrame(() => { messages.scrollTop = messages.scrollHeight; }); }
  function addMessage(role, text, products) {
    const isUser = role === 'user';
    const message = element('div', 'shopping-assistant__message' + (isUser ? ' shopping-assistant__message--user' : ''));
    message.append(element('span', 'shopping-assistant__speaker', isUser ? 'Tú' : 'Casi Tú · Asistente IA'));
    const bubble = element('div', 'shopping-assistant__bubble');
    richText(bubble, text);
    if (!isUser) addProducts(bubble, products);
    message.append(bubble);
    messages.append(message);
    scrollLatest();
    return message;
  }
  function greeting() { addMessage('assistant', '¡Hola! ✨ ¿Buscas un regalo de Navidad, una figura a partir de tus fotos o un modelo ya diseñado? Cuéntame para quién es y te ayudo a elegir. Si quieres que el equipo te responda por correo, pulsa «Dejar mi correo».'); }
  function setTyping(value) {
    typing?.remove();
    typing = null;
    if (value) {
      typing = element('div', 'shopping-assistant__typing', 'Estoy preparando tu respuesta…');
      typing.setAttribute('role', 'status');
      messages.append(typing);
      scrollLatest();
    }
  }
  function controls() {
    send.disabled = busy || !!pending || finished || !token || !restored || !input.value.trim();
    input.disabled = busy || !!pending || finished || !token || !restored;
    start.disabled = busy;
    q('[data-chat-contact]').disabled = busy || !!pending || finished;
    q('[data-chat-finish]').disabled = busy || !!pending || finished;
    q('[data-chat-new]').disabled = busy || !!pending;
    q('[data-chat-handoff-submit]').disabled = busy || sentToTeam;
    q('[data-chat-delete]').disabled = busy;
    quick.querySelectorAll('button').forEach(button => { button.disabled = busy || !!pending || finished || !restored; });
    q('[data-chat-form]').setAttribute('aria-busy', String(busy));
  }
  function showConversation() {
    welcome.hidden = true;
    conversation.hidden = false;
    controls();
  }
  function expire() {
    // Keep the bearer until an explicit deletion: delivery copies can outlive the active session.
    pending = null;
    restored = true;
    finished = true;
    sentToTeam = false;
    setTyping(false);
    messages.replaceChildren();
    input.value = '';
    input.style.height = '';
    quick.hidden = true;
    handoff.hidden = true;
    handoff.reset();
    confirm.hidden = true;
    showConversation();
    showNotice('La sesión ha caducado. Pulsa «Nueva conversación» para confirmar el borrado del historial y las copias pendientes del asistente antes de empezar otra. Esto no elimina correos ya enviados.');
  }
  async function request(path, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 35000);
    const headers = {Accept:'application/json'};
    if (token) headers.Authorization = 'Bearer ' + token;
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    try {
      const response = await fetch(endpoint + path, {
        method:options.method || 'GET', headers, credentials:'omit', cache:'no-store', signal:controller.signal,
        body:options.body === undefined ? undefined : JSON.stringify(options.body)
      });
      let data = {};
      if (response.status !== 204) {
        try { data = await response.json(); } catch (_) { throw new Error('No hemos podido leer la respuesta. Vuelve a intentarlo.'); }
      }
      if (!response.ok) {
        const error = new Error(typeof data.error?.message === 'string' ? data.error.message.slice(0, 240) : 'No hemos podido completar la solicitud. Inténtalo de nuevo en un momento.');
        error.code = data.error?.code;
        error.status = response.status;
        throw error;
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError' || error instanceof TypeError) throw new Error('La conexión se ha interrumpido. Puedes reintentar sin duplicar tu mensaje.');
      throw error;
    } finally { clearTimeout(timeout); }
  }
  async function restore() {
    if (!token || restored) return;
    if (restorePromise) return restorePromise;
    restorePromise = (async () => {
      busy = true;
      showConversation();
      showNotice('Recuperando tu conversación…');
      try {
        const data = await request('/v1/session');
        if (!Array.isArray(data.messages)) throw new Error('No hemos podido recuperar la conversación. Inténtalo de nuevo.');
        messages.replaceChildren();
        data.messages.slice(-100).forEach(message => {
          if ((message.role === 'user' || message.role === 'assistant') && typeof message.content === 'string') addMessage(message.role, message.content, message.products);
        });
        if (!messages.childElementCount) greeting();
        quick.hidden = data.messages.some(message => message.role === 'user');
        restored = true;
        finished = data.ended === true || data.status === 'finished';
        showNotice(finished ? 'Esta conversación ha finalizado. Puedes empezar otra cuando quieras.' : '');
      } catch (error) {
        if (error.status === 401 || error.status === 404 || error.code === 'session_expired') expire();
        else showNotice(error.message, restore);
      } finally { busy = false; restorePromise = null; controls(); }
    })();
    return restorePromise;
  }
  async function begin() {
    if (busy || token) return;
    busy = true;
    controls();
    showNotice('Conectando con tu asistente…');
    try {
      const data = await request('/v1/session', {method:'POST', body:{consent:true}});
      if (typeof data.token !== 'string' || !/^[A-Za-z0-9._~-]{20,2048}$/.test(data.token)) throw new Error('No hemos podido iniciar el chat. Puedes contactar con nosotros desde la página de contacto.');
      remember(data.token);
      restored = true;
      finished = false;
      sentToTeam = false;
      messages.replaceChildren();
      greeting();
      quick.hidden = false;
      showConversation();
      showNotice('');
    } catch (error) { showNotice(error.message, begin); }
    finally { busy = false; controls(); }
  }
  async function deliver() {
    if (!pending || busy) return;
    busy = true;
    controls();
    showNotice('');
    setTyping(true);
    try {
      const data = await request('/v1/message', {method:'POST', body:{message:pending.message, requestId:pending.requestId, pagePath:pending.pagePath}});
      if (typeof data.reply !== 'string' || !data.reply.trim()) throw new Error('La respuesta no ha llegado completa. Reintenta recuperar tu mensaje.');
      setTyping(false);
      addMessage('assistant', data.reply, data.products);
      pending = null;
      sentToTeam = false;
    } catch (error) {
      setTyping(false);
      if (error.status === 401 || error.code === 'session_expired') expire();
      else if (['session_ended', 'request_conflict', 'conversation_limit', 'invalid_message'].includes(error.code)) {
        pending.row?.append(element('span', 'shopping-assistant__delivery', 'No se ha confirmado el envío de este mensaje.'));
        if (error.code === 'invalid_message') input.value = pending.message;
        if (error.code === 'session_ended') finished = true;
        pending = null;
        showNotice(error.message);
      }
      else showNotice(error.message, deliver);
    } finally { busy = false; controls(); }
  }
  function ask(value) {
    const text = value.trim();
    if (!text || text.length > 2000 || busy || pending || finished || !token || !restored) return;
    pending = {message:text, requestId:crypto.randomUUID(), pagePath:location.pathname, row:addMessage('user', text)};
    input.value = '';
    input.style.height = '';
    quick.hidden = true;
    handoff.hidden = true;
    confirm.hidden = true;
    deliver();
  }
  function open() {
    if (dialog.open) return;
    returnFocus = document.activeElement;
    dialog.showModal();
    document.body.classList.add('shopping-assistant-open');
    q('[data-chat-open]').setAttribute('aria-expanded', 'true');
    q('[data-chat-close]').focus();
    restore();
    scrollLatest();
    fitViewport();
  }
  function fitViewport() {
    if (!dialog.open || !matchMedia('(max-width:749px)').matches || !window.visualViewport) {
      dialog.style.removeProperty('--chat-viewport-height');
      dialog.style.removeProperty('--chat-viewport-top');
      return;
    }
    dialog.style.setProperty('--chat-viewport-height', Math.round(window.visualViewport.height) + 'px');
    dialog.style.setProperty('--chat-viewport-top', Math.round(window.visualViewport.offsetTop) + 'px');
  }
  function close() { dialog.close(); }
  q('[data-chat-open]').addEventListener('click', open);
  q('[data-chat-close]').addEventListener('click', close);
  dialog.addEventListener('click', event => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close(); } });
  dialog.addEventListener('close', () => {
    document.body.classList.remove('shopping-assistant-open');
    q('[data-chat-open]').setAttribute('aria-expanded', 'false');
    returnFocus?.focus({preventScroll:true});
  });
  window.addEventListener('resize', fitViewport, {passive:true});
  window.visualViewport?.addEventListener('resize', fitViewport, {passive:true});
  window.visualViewport?.addEventListener('scroll', fitViewport, {passive:true});
  start.addEventListener('click', begin);
  q('[data-chat-form]').addEventListener('submit', event => { event.preventDefault(); ask(input.value); });
  input.addEventListener('input', () => { input.style.height = 'auto'; input.style.height = Math.min(input.scrollHeight, 110) + 'px'; controls(); });
  input.addEventListener('keydown', event => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && !matchMedia('(pointer:coarse)').matches) { event.preventDefault(); ask(input.value); }
  });
  quick.querySelectorAll('[data-chat-prompt]').forEach(button => button.addEventListener('click', () => ask(button.dataset.chatPrompt)));
  q('[data-chat-contact]').addEventListener('click', () => {
    handoff.hidden = !handoff.hidden;
    confirm.hidden = true;
    if (!handoff.hidden) handoff.querySelector('input').focus();
  });
  q('[data-chat-handoff-close]').addEventListener('click', () => { handoff.hidden = true; q('[data-chat-contact]').focus(); });
  handoff.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy || sentToTeam || !token || !handoff.reportValidity()) return;
    busy = true;
    controls();
    showNotice('Solicitando el envío al equipo…');
    try {
      const email = handoff.querySelector('input').value.trim();
      await request('/v1/handoff', {method:'POST', body:email ? {email} : {}});
      sentToTeam = true;
      handoff.hidden = true;
      handoff.reset();
      showNotice('Hemos solicitado el envío de esta conversación al equipo.' + (email ? ' Has dejado tu email para que puedan responderte.' : ' Si necesitas respuesta por correo, utiliza también nuestra página de contacto.'));
    } catch (error) {
      if (error.status === 401 || error.code === 'session_expired') expire();
      else showNotice(error.message);
    }
    finally { busy = false; controls(); }
  });
  q('[data-chat-finish]').addEventListener('click', async () => {
    if (busy || pending || !token || finished) return;
    busy = true;
    controls();
    showNotice('Terminando la conversación…');
    try {
      await request('/v1/finish', {method:'POST', body:{}});
      finished = true;
      handoff.hidden = true;
      showNotice('Conversación finalizada; hemos solicitado su envío al equipo. ¡Gracias por pasarte por Casi Tú 3D!');
    } catch (error) {
      if (error.status === 401 || error.code === 'session_expired') expire();
      else showNotice(error.message);
    }
    finally { busy = false; controls(); }
  });
  q('[data-chat-new]').addEventListener('click', () => { confirm.hidden = false; handoff.hidden = true; q('[data-chat-keep]').focus(); });
  q('[data-chat-keep]').addEventListener('click', () => { confirm.hidden = true; q('[data-chat-new]').focus(); });
  q('[data-chat-delete]').addEventListener('click', async () => {
    if (busy || pending) return;
    busy = true;
    controls();
    try {
      if (token) { try { await request('/v1/session', {method:'DELETE'}); } catch (error) { if (![401,404].includes(error.status)) throw error; } }
      remember('');
      restored = false;
      finished = false;
      sentToTeam = false;
      messages.replaceChildren();
      input.value = '';
      handoff.reset();
      confirm.hidden = true;
      conversation.hidden = true;
      welcome.hidden = false;
      showNotice('');
      start.focus();
    } catch (error) { showNotice(error.message); }
    finally { busy = false; controls(); }
  });
  controls();
})();
